import clsx from 'clsx';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Avatar } from 'antd';
import { useTranslation } from 'react-i18next';
import { Icon } from '@icp/components';
import PropTypes from 'prop-types';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { useClassName } from '../../hooks';
import FieldTitle from '../../FieldTitle';
import UserAvatarEditModal from './UserAvatarEditModal';
import { ConditionalPropertyPropType } from '../../propTypes';
import { withFieldWrapper } from '../../fieldWrapper';

const UserAvatarElement = forwardRef(function UserAvatarElement(props, ref) {
  const {
    keyPath,
    id,
    title,
    value,
    disabled,
    readonly,
    className: classNameProp,
    style,
    componentProps = {},
    fieldTitleProps = { hidden: true },
    validation,
    helpers,
    onChange,
  } = props;

  const {
    size = 128,
    shape = 'circle',
    fileSizeLimit,
    className: compClass,
    style: compStyle,
    ...otherProps
  } = componentProps;

  const avatarUrl = useMemo(() => {
    const { resources } = value || {};
    return resources?.[0]?.url;
  }, [value]);

  const { t } = useTranslation(['icp-form-renderer']);
  const [openEditModal, setOpenEditModal] = useState(false);

  const ElementDecorator = useElementDecorator();
  const className = useClassName(classNameProp);
  const classNameComp = useClassName(compClass);
  const isInDesign = useIsInDesign();
  const editable = !readonly && !disabled && !isInDesign;
  const nodeRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
    }),
    [],
  );

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx(
          'user-avatar-element',
          'input-element',
          'form-element',
          { 'has-helper': !helpersIsEmpty(helpers) },
          className,
        )}
        style={{ width: size, ...style }}
        ref={nodeRef}
      >
        <FieldTitle required={validation?.required} {...fieldTitleProps}>
          {title}
        </FieldTitle>
        <Avatar
          {...otherProps}
          alt={t('user-avatar.title')}
          icon={<Icon name="ant:user-outlined" size={size * 0.75} />}
          src={avatarUrl}
          shape={shape}
          size={size}
          className={classNameComp}
          style={{ cursor: editable ? 'pointer' : 'default', ...compStyle }}
          onClick={editable ? () => setOpenEditModal(true) : null}
        />
        {!disabled && !readonly && (
          <UserAvatarEditModal
            title={title || t('user-avatar.title')}
            shape={shape}
            sizeLimit={fileSizeLimit}
            value={value}
            open={openEditModal}
            onClose={() => setOpenEditModal(false)}
            onChange={onChange}
          />
        )}
      </div>
    </ElementDecorator>
  );
});

UserAvatarElement.propTypes = {
  keyPath: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.shape({
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        storageId: PropTypes.string,
        fileName: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
  }),
  componentProps: PropTypes.shape({
    size: PropTypes.number,
    shape: PropTypes.oneOf(['circle', 'square']),
    fileSizeLimit: PropTypes.number,
    className: PropTypes.string,
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
  onChange: PropTypes.func,
};

UserAvatarElement.displayName = 'UserAvatar';

export default withFieldWrapper(UserAvatarElement);
